import { useCallback, useEffect, useState } from 'react';
import { Archive, ArchiveRestore, Pencil, Trash2, X } from 'lucide-react';
import { api } from '../api/client';
import { ScenarioBudget } from '../types';

interface Props {
  onFermer: () => void;
  onModifie: () => void; // recharger la grille (scénarios changés)
}

/**
 * Gestion des scénarios nommés de projection. Supprimer un scénario supprime
 * aussi tous ses événements projetés (cascade) — d'où la double confirmation.
 *
 * Un scénario créé ici est **dérivé du socle** : sans parent, il n'hériterait
 * d'aucune hypothèse (ni les charges ni les revenus projetés du budget) et se
 * lirait comme les décisions seules. Le socle et les parents, eux, ne
 * s'archivent ni ne se suppriment : leurs descendants lisent leurs projections
 * par héritage.
 */
export function ScenariosModal({ onFermer, onModifie }: Props) {
  const [scenarios, setScenarios] = useState<ScenarioBudget[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [nom, setNom] = useState('');
  const [description, setDescription] = useState('');
  const [enCours, setEnCours] = useState(false);

  const charger = useCallback(() => {
    api.get<ScenarioBudget[]>('/revenus/scenarios')
      .then(setScenarios)
      .catch((e) => setErreur(e.message));
  }, []);

  useEffect(() => { charger(); }, [charger]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onFermer(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onFermer]);

  const socle = scenarios.find((s) => s.socle && !s.archive) ?? null;

  async function creer() {
    if (!nom.trim() || enCours) return;
    setEnCours(true);
    setErreur(null);
    try {
      // Créé comme variante du socle (héritage) — un scénario orphelin ne
      // porterait aucune des hypothèses du budget.
      await api.post('/scenarios', {
        nom: nom.trim(), description: description.trim() || null,
        parentId: socle?.id ?? null,
      });
      setNom('');
      setDescription('');
      charger();
      onModifie();
    } catch (e) {
      setErreur((e as Error).message);
    } finally {
      setEnCours(false);
    }
  }

  async function renommer(s: ScenarioBudget) {
    const nouveau = window.prompt(`Nouveau nom pour « ${s.nom} » :`, s.nom);
    if (!nouveau || !nouveau.trim() || nouveau.trim() === s.nom) return;
    setErreur(null);
    try {
      await api.put(`/revenus/scenarios/${s.id}`, { nom: nouveau.trim() });
      charger();
      onModifie();
    } catch (e) {
      setErreur((e as Error).message);
    }
  }

  async function archiver(s: ScenarioBudget) {
    setErreur(null);
    try {
      await api.put(`/revenus/scenarios/${s.id}`, { archive: !s.archive });
      charger();
      onModifie();
    } catch (e) {
      setErreur((e as Error).message);
    }
  }

  async function supprimer(s: ScenarioBudget) {
    const nb = s.nb_evenements ?? 0;
    if (!window.confirm(
      `Supprimer le scénario « ${s.nom} »${nb > 0 ? ` et ses ${nb} événement${nb > 1 ? 's' : ''} projeté${nb > 1 ? 's' : ''}` : ''} ? Cette action est irréversible.`
    )) return;
    const c = window.prompt('Commentaire de suppression (obligatoire) :');
    if (!c || !c.trim()) return;
    setErreur(null);
    try {
      await api.delete(`/revenus/scenarios/${s.id}`, { commentaire: c.trim() });
      charger();
      onModifie();
    } catch (e) {
      setErreur((e as Error).message);
    }
  }

  return (
    <div className="modal-overlay" onClick={onFermer}>
      <div className="modal" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div className="modal-title">Scénarios de projection</div>
            <div className="text-xs text-ink-3">
              Un scénario regroupe des hypothèses projetées ; la grille affiche les décisions
              définitives plus les projections du scénario choisi.
            </div>
          </div>
          <button className="close-btn" onClick={onFermer} title="Fermer (Esc)"><X className="icon-sm" /></button>
        </div>

        <div className="modal-body" style={{ overflowY: 'auto' }}>
          {erreur && <div className="text-err mb-16">{erreur}</div>}

          <div className="formulaire-changement mb-16">
            <div className="formulaire-changement__titre">Nouveau scénario</div>
            <div className="flex gap-12 flex-wrap items-end">
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Nom</label>
                <input className="form-input" style={{ width: 180 }} placeholder="Ex. Prix OAI renégociés"
                  value={nom} onChange={(e) => setNom(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') creer(); }} />
              </div>
              <div className="form-group" style={{ flex: 1, minWidth: 160, marginBottom: 0 }}>
                <label className="form-label">Description</label>
                <input className="form-input w-full" placeholder="Hypothèses du scénario…"
                  value={description} onChange={(e) => setDescription(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') creer(); }} />
              </div>
              <button className="btn btn-accent" disabled={!nom.trim() || enCours} onClick={creer}>
                {enCours ? 'Création…' : 'Créer'}
              </button>
            </div>
            <div className="text-xs text-ink-3 mt-8">
              {socle
                ? <>Créé comme variante du socle « {socle.nom} » : il en hérite toutes les hypothèses
                    et suit ses évolutions — n'y saisir que les écarts.</>
                : <>Aucun socle défini : ce scénario ne portera aucune hypothèse héritée
                    (désigner un socle dans <a href="#scenarios" className="text-accent">Scénarios</a>).</>}
            </div>
          </div>

          <table className="table table-compact">
            <thead>
              <tr><th>Scénario</th><th>Événements</th><th>Statut</th><th style={{ width: 110 }} /></tr>
            </thead>
            <tbody>
              {scenarios.map((s) => {
                // Socle et parents ne se retirent pas : leurs descendants lisent
                // leurs projections par héritage (mêmes règles que la section
                // Scénarios ; on désactive ici pour que la raison se lise avant
                // le clic).
                const enfants = scenarios.filter((x) => x.parent_id === s.id);
                const verrou = s.socle
                  ? `« ${s.nom} » est le socle du budget : toutes les vues s'ouvrent dessus et `
                    + `les variantes en héritent. Désignez d'abord un autre socle dans la section Scénarios.`
                  : enfants.length
                    ? `Parent de ${enfants.map((e) => `« ${e.nom} »`).join(', ')} : réaffectez `
                      + `${enfants.length > 1 ? 'ces scénarios' : 'ce scénario'} dans la section Scénarios.`
                    : null;
                return (
                <tr key={s.id} style={s.archive ? { opacity: 0.55 } : undefined}>
                  <td>
                    <span className="font-medium">{s.nom}</span>
                    {s.socle && <span className="badge badge-accent badge-sm" title="Socle du budget">socle</span>}
                    {s.description && <span className="text-xs text-ink-3"> — {s.description}</span>}
                  </td>
                  <td className="tabular-nums">{s.nb_evenements ?? 0}</td>
                  <td>{s.archive
                    ? <span className="badge badge-sm">archivé</span>
                    : <span className="badge badge-ok badge-sm">actif</span>}
                  </td>
                  <td>
                    <div className="flex gap-4 justify-end">
                      <button className="btn btn-icon" title="Renommer" onClick={() => renommer(s)}>
                        <Pencil className="icon-sm" />
                      </button>
                      <button className="btn btn-icon"
                        disabled={!s.archive && verrou != null}
                        title={s.archive ? 'Restaurer' : verrou ?? 'Archiver (masque le scénario sans le supprimer)'}
                        onClick={() => archiver(s)}>
                        {s.archive ? <ArchiveRestore className="icon-sm" /> : <Archive className="icon-sm" />}
                      </button>
                      <button className="btn btn-icon"
                        disabled={verrou != null}
                        title={verrou ?? 'Supprimer le scénario et ses événements'}
                        onClick={() => supprimer(s)}>
                        <Trash2 className="icon-sm" />
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
              {scenarios.length === 0 && (
                <tr><td colSpan={4} className="text-ink-3">Aucun scénario — en créer un pour peindre des projections.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
